import { BaseModelWithFiles } from "@/models/base-with-files.model"
import type {
  IApprovalHistory,
  IApprovalRequirements,
  ICustomerPurchaseOrder
} from "@/interfaces/api/customer-purchase-order.interface"
import type { ApprovalMethod } from "@/interfaces/api/known-business-entity.interface"
import type { Moment } from "moment"
import type { ICustomerInvoiceSummary } from "@/interfaces/api/customer-invoice.interface"
import { KnownBusinessEntity } from "@/models/known-business-entity.model"
import { ModelFactory } from "@/factories/model-factory"
import { ProductionStatementCustomerPurchaseOrder } from "@/models/production-statement-customer-purchase-order.model"
import type { FileRelation } from "@/models/file/file-relation.model.ts"
import { toCurrencyCents } from "@/components/customer_purchase_order/customer-purchase-order-matching"

export class CustomerPurchaseOrder extends BaseModelWithFiles implements ICustomerPurchaseOrder {
  public static className = "CustomerPurchaseOrder"
  public static verboseName = "Akkoord klant"

  supplierName?: string
  supplier?: KnownBusinessEntity | null
  reference: string | null
  reference2: string
  amountExclVat: string | null
  amountExclVatRounded?: string | null
  approvalMethod: ApprovalMethod
  approvedByName: string
  approvalDate: Moment | null
  note: string
  invoiceNote: string
  deletedAt: Moment | null
  processedAt: Moment | null
  completedAt: Moment | null
  completedByName: string
  invoicedAt: Moment | null
  invoicedByName: string
  invoiceNumber: string | null
  invoice: ICustomerInvoiceSummary | null
  approvalRevision: number
  approvalRequirements?: IApprovalRequirements
  approvalHistory: IApprovalHistory[]
  allocationCount?: number
  matchedAmountSum?: string
  productionStatementCustomerPurchaseOrders: ProductionStatementCustomerPurchaseOrder[]

  constructor(customerPurchaseOrder: ICustomerPurchaseOrder, managed = false) {
    super(customerPurchaseOrder, managed)

    this.supplierName = customerPurchaseOrder.supplierName
    this.supplier = ModelFactory.childModel(
      KnownBusinessEntity,
      customerPurchaseOrder.supplier,
      managed
    )
    this.reference = customerPurchaseOrder.reference
    this.reference2 = customerPurchaseOrder.reference2 ?? ""
    this.amountExclVat = customerPurchaseOrder.amountExclVat ?? null
    this.amountExclVatRounded = customerPurchaseOrder.amountExclVatRounded
    this.approvalMethod = customerPurchaseOrder.approvalMethod ?? "purchase_order"
    this.approvedByName = customerPurchaseOrder.approvedByName ?? ""
    this.approvalDate = customerPurchaseOrder.approvalDate ?? null
    this.note = customerPurchaseOrder.note ?? ""
    this.invoiceNote = customerPurchaseOrder.invoiceNote ?? ""
    this.deletedAt = customerPurchaseOrder.deletedAt ?? null
    this.processedAt = customerPurchaseOrder.processedAt ?? null
    this.completedAt = customerPurchaseOrder.completedAt ?? null
    this.completedByName = customerPurchaseOrder.completedByName ?? ""
    this.invoicedAt = customerPurchaseOrder.invoicedAt ?? null
    this.invoicedByName = customerPurchaseOrder.invoicedByName ?? ""
    this.invoiceNumber = customerPurchaseOrder.invoiceNumber ?? null
    this.invoice = customerPurchaseOrder.invoice ?? null
    this.approvalRevision = customerPurchaseOrder.approvalRevision ?? 0
    this.approvalRequirements = customerPurchaseOrder.approvalRequirements
    this.approvalHistory = customerPurchaseOrder.approvalHistory ?? []
    this.allocationCount = customerPurchaseOrder.allocationCount
    this.matchedAmountSum = customerPurchaseOrder.matchedAmountSum
    this.productionStatementCustomerPurchaseOrders = ModelFactory.childModels(
      ProductionStatementCustomerPurchaseOrder,
      customerPurchaseOrder.productionStatementCustomerPurchaseOrders ?? [],
      managed
    )
  }

  public getSupplierName(): string {
    return this.supplier?.name || this.supplierName || ""
  }

  public getAllocatedAmount(): number {
    if (this.isShallow() && this.matchedAmountSum !== undefined) {
      return Number(this.matchedAmountSum)
    }
    return this.productionStatementCustomerPurchaseOrders.reduce(
      (sum, allocation) => sum + Number(allocation.amount),
      0
    )
  }

  public getOutstandingAmount(): number {
    if (!this.hasSourceAmount()) return 0
    const sourceAmount =
      this.isShallow() && this.amountExclVatRounded !== undefined
        ? this.amountExclVatRounded
        : this.amountExclVat
    return (toCurrencyCents(sourceAmount) - toCurrencyCents(this.getAllocatedAmount())) / 100
  }

  public getAllocationCount(): number {
    return this.isShallow()
      ? (this.allocationCount ?? 0)
      : this.productionStatementCustomerPurchaseOrders.length
  }

  public isOutstanding(): boolean {
    return (
      !this.processedAt ||
      this.getAllocationCount() === 0 ||
      (this.hasSourceAmount() && Math.abs(this.getOutstandingAmount()) >= 0.005)
    )
  }

  public getStatusLabel(): string {
    if (!this.processedAt) return "Nog vast te leggen"
    if (this.getAllocationCount() === 0) return "Ingetrokken"
    return this.isOutstanding() ? "Deels verwerkt" : "Verwerkt"
  }

  public hasSourceAmount(): boolean {
    return this.amountExclVat !== null && this.amountExclVat !== ""
  }

  public hasApprovalContent(): boolean {
    return Boolean(
      this.supplier ||
      this.reference?.trim() ||
      this.reference2.trim() ||
      this.hasSourceAmount() ||
      this.approvedByName.trim() ||
      this.approvalDate ||
      this.note.trim() ||
      this.invoiceNote.trim() ||
      this.fileBucket.fileRelations.length ||
      this.productionStatementCustomerPurchaseOrders.length ||
      this.processedAt
    )
  }

  public getApprovalRequirements(): IApprovalRequirements {
    if (this.processedAt && this.approvalRequirements) return this.approvalRequirements
    const client = this.supplier
    const noApprovalRequired = !client?.approvalMode || client.approvalMode === "none"
    return {
      amountRequired: !noApprovalRequired && (client?.approvalAmountRequired ?? false),
      fileRequired: !noApprovalRequired && (client?.approvalFileRequired ?? false),
      referenceRequired:
        !noApprovalRequired &&
        Boolean(client?.approvalReferenceLabel.trim()) &&
        (client?.approvalReferenceRequired ?? false),
      referenceLabel: noApprovalRequired ? "" : (client?.approvalReferenceLabel ?? ""),
      referenceFormat:
        noApprovalRequired || !client?.approvalReferenceLabel.trim()
          ? null
          : client.approvalReferenceFormat,
      reference2Required:
        !noApprovalRequired &&
        Boolean(client?.approvalReference2Label.trim()) &&
        (client?.approvalReference2Required ?? false),
      reference2Label: noApprovalRequired ? "" : (client?.approvalReference2Label ?? ""),
      reference2Format:
        noApprovalRequired || !client?.approvalReference2Label.trim()
          ? null
          : client.approvalReference2Format
    }
  }

  public getTitle(): string {
    return (
      this.getSupplierName() ||
      this.getFileRelation()?.file.getOriginalFileNameIncludingExtension() ||
      "Nieuw akkoord"
    )
  }

  public getFileRelation(): FileRelation | undefined {
    return this.fileBucket?.fileRelations[0]
  }
}
